import { flexRender } from "@tanstack/react-table";
import type React from "react";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Skeleton } from "@/design-system/Skeleton";
import { twResolve } from "@/helpers/tw-resolve";
import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

import { useCustomTable } from "./CustomTableContext";
import { type DashboardCell, type DashboardRow, getTableCellWidth } from "./helpers";

type TableBodyCellProps<TColumnData extends {}> = {
  cell: DashboardCell<TColumnData>;
  isLastColumn: boolean;
  onRowClick?: (row: DashboardRow<TColumnData>) => void;
  rowHref?: string;
  isLoading?: boolean;
};

export function TableBodyCell<TColumnData extends {}>({
  cell,
  isLastColumn,
  rowHref,
  onRowClick,
  isLoading,
}: TableBodyCellProps<TColumnData>): React.ReactNode {
  // oxlint-disable-next-line react-hooks/purity
  const [skeletonWidth] = useState(Math.max(Math.random() * cell.column.getSize() * 0.75, 36));
  const { unfilledWidth } = useCustomTable();

  const isCellSimulatingRowClick = cell.column.columnDef.meta?.shouldSimulateRowClick && (onRowClick || rowHref);
  const isCellVisualIndicator = cell.column.columnDef.meta?.isVisualIndicator;
  const isCellAction = cell.column.columnDef.meta?.isAction;
  const cellAlignment = cell.column.columnDef.meta?.cellAlignment;

  const shouldCellTruncate = cell.column.columnDef.meta?.shouldTruncate ?? (!isCellVisualIndicator && !isCellAction);

  let content = <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>;
  if (shouldCellTruncate) {
    content = (
      <span
        className={twResolve(
          "block truncate",
          cellAlignment === "center" && "flex justify-center",
          cellAlignment === "end" && "flex justify-end",
        )}
      >
        {content}
      </span>
    );
  }
  const width = getTableCellWidth(cell) + (isLastColumn ? unfilledWidth : 0);

  return (
    <RuntimeWidth width={width}>
      {(widthAttributes) => (
        <td
          {...widthAttributes}
          className={twResolve(
            "h-table-row border-r border-border-default p-space-sm text-left last:border-r-0 last:align-middle last:*:ml-auto @md:px-space-md",
            isCellVisualIndicator && "border-r-0 p-table-reset!",
            cellAlignment === "center" && "text-center",
            cellAlignment === "end" && "text-right",
          )}
          key={cell.id}
        >
          {isLoading && !isCellAction && (
            <RuntimeWidth width={skeletonWidth}>
              {(skeletonWidthAttributes) => (
                <div {...skeletonWidthAttributes}>
                  <Skeleton height="4" />
                </div>
              )}
            </RuntimeWidth>
          )}
          {!isLoading && (
            <>
              {isCellSimulatingRowClick && !rowHref && onRowClick && (
                <Button variant="ghostPrimary" type="button" size="sm" onClick={() => onRowClick?.(cell.row)}>
                  {content}
                </Button>
              )}
              {isCellSimulatingRowClick && rowHref && (
                <Button
                  variant="ghostPrimary"
                  type="link"
                  size="sm"
                  onClick={() => onRowClick?.(cell.row)}
                  href={rowHref}
                >
                  {content}
                </Button>
              )}
              {!isCellSimulatingRowClick && !isCellAction && content}
              {!isCellSimulatingRowClick && isCellAction && <div className="ml-auto w-fit">{content}</div>}
            </>
          )}
        </td>
      )}
    </RuntimeWidth>
  );
}
